// src/components/BuyGhsx.js
import React, { useState, useMemo } from "react";
import { buyGHSX } from "../components/ghsx";

const BackChevron = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center justify-center rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
    aria-label="Back"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  </button>
);

const OPTION_LABELS = {
  fixed: "Fixed rate",
  configured: "Special rate",
};

/**
 * Turns the /api/ghsx/rate payload into a list of selectable options.
 * Rates are priced in GHS per 1 GHSX. Nothing is hard coded here.
 */
function buildOptions(pricing) {
  if (!pricing) return [];

  return ["fixed", "configured"]
    .map((key) => {
      const p = pricing[key];
      if (!p) return null;

      const rate = Number(p.rate);
      if (!Number.isFinite(rate) || rate <= 0) return null;

      const min = Number(p.minPurchaseAmount ?? p.minimumPurchaseAmount);

      return {
        key,
        label: OPTION_LABELS[key],
        rate, // GHS per GHSX
        currency: p.currency || "GHS",
        min: Number.isFinite(min) && min > 0 ? min : null,
      };
    })
    .filter(Boolean);
}

/**
 * BuyGhsx
 *
 * Props:
 *   usdt         — { available } from parent
 *   ghsxPricing  — response `data` of GET /api/ghsx/rate
 *                  (optionally includes `ghsPerUsdt`, the live GHS-per-USDT
 *                   rate, so the USDT cost can be previewed)
 *   onBack       — () => void
 *   onSuccess    — () => void
 *
 * The user picks one of the backend-provided rates. The chosen
 * `pricingOption` is sent with the purchase and the backend recalculates the
 * real USDT cost, so any preview here is only an estimate.
 */
export function BuyGhsx({ usdt, ghsxPricing, onBack, onSuccess }) {
  /* view: "buy-method" | "buy-rate" | "buy-input" | "buy-confirm" | "buy-success" */
  const [view,          setView]          = useState("buy-method");
  const [paymentMethod, setPaymentMethod] = useState("usdt");
  const [currency]                        = useState("USD");
  const [pricingOption, setPricingOption] = useState(null);
  const [buyAmount,     setBuyAmount]     = useState("");
  const [buyError,      setBuyError]      = useState(null);
  const [buyLoading,    setBuyLoading]    = useState(false);
  const [buyResult,     setBuyResult]     = useState(null);
  const [errors,        setErrors]        = useState({});

  const available = usdt?.available ?? 0;

  const options  = useMemo(() => buildOptions(ghsxPricing), [ghsxPricing]);
  const selected = options.find((o) => o.key === pricingOption) || null;
  const minAmt   = selected?.min ?? null;

  /* GHS per 1 USDT — supplied by the backend, never hard coded here. */
  const ghsPerUsdt = Number(ghsxPricing?.ghsPerUsdt);
  const hasFx      = Number.isFinite(ghsPerUsdt) && ghsPerUsdt > 0;

  /* USDT price per 1 GHSX at the selected rate (null if FX unknown). */
  const priceUsdt = selected && hasFx
    ? Number((selected.rate / ghsPerUsdt).toFixed(6))
    : null;

  const costUsdt = (amt) =>
    priceUsdt != null ? Number((Number(amt) * priceUsdt).toFixed(6)) : null;

  const costGhs = (amt) =>
    selected ? Number((Number(amt) * selected.rate).toFixed(2)) : null;

  const resetInput = () => {
    setBuyAmount("");
    setBuyError(null);
    setErrors({});
  };

  const handleSelectMethod = (method) => {
    setPaymentMethod(method);
    resetInput();
    if (method !== "usdt") return;

    if (options.length === 1) {
      setPricingOption(options[0].key);
      setView("buy-input");
    } else {
      setPricingOption(null);
      setView("buy-rate");
    }
  };

  const handleSelectRate = (key) => {
    setPricingOption(key);
    resetInput();
    setView("buy-input");
  };

  const handleBackFromInput = () => {
    resetInput();
    setView(options.length > 1 ? "buy-rate" : "buy-method");
  };

  const handleMax = () => {
    if (!priceUsdt) return;
    const maxAmount = Math.max(
      0,
      Number((Math.floor((available / priceUsdt) * 100) / 100).toFixed(2))
    );
    if (maxAmount > 0) {
      setBuyAmount(String(maxAmount));
      setErrors((e) => ({ ...e, amount: null }));
      setBuyError(null);
    }
  };

  /* Balance check only runs when the FX rate is known; otherwise the
     backend performs the authoritative check. */
  const validateAmount = () => {
    const numeric = Number(buyAmount);
    if (!buyAmount || numeric <= 0) return "Enter a valid amount";
    if (minAmt && numeric < minAmt) return `Minimum purchase for this rate is ${minAmt} GHSX.`;
    const cost = costUsdt(numeric);
    if (cost != null && cost > Number(available))
      return `Insufficient USDT balance. You have ${Number(available).toFixed(2)} USDT.`;
    return null;
  };

  const handleBuy = async () => {
    const msg = validateAmount();
    if (msg) {
      setBuyError(msg);
      return;
    }

    setBuyError(null);
    setBuyLoading(true);

    try {
      const result = await buyGHSX({
        amount: Number(buyAmount),
        paymentMethod,
        currency,
        pricingOption, // "fixed" | "configured"
      });

      setBuyResult(result);
      setView("buy-success");
      onSuccess?.();
    } catch (err) {
      setBuyError(err?.error || err?.message || "Purchase failed");
    } finally {
      setBuyLoading(false);
    }
  };

  const Header = ({ title, onClick }) => (
    <div className="border-b border-neutral-200 p-4 dark:border-neutral-700">
      <div className="flex items-center gap-3">
        <BackChevron onClick={onClick} />
        <h2 className="text-base font-semibold text-orange-400">{title}</h2>
      </div>
    </div>
  );

  const fmtUsdt = (v) => (v == null ? "—" : Number(v).toFixed(4));

  /* ============================================================
     VIEW: buy-method
  ============================================================ */
  if (view === "buy-method") {
    const noRates = options.length === 0;

    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        <Header title="Buy GHSX" onClick={onBack} />

        <div className="flex flex-col gap-6 p-6">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Select how you'd like to pay for GHSX.
          </p>

          {noRates && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Purchase rates are not available right now. Please try again shortly.
            </p>
          )}

          <div className="flex flex-col gap-3">
            <button
              onClick={() => handleSelectMethod("usdt")}
              disabled={noRates}
              className="flex w-full items-center justify-between gap-3 rounded-xl border
                         border-neutral-300 px-4 py-4 text-left transition-colors
                         hover:border-orange-400 hover:bg-orange-50 active:bg-orange-100
                         disabled:cursor-not-allowed disabled:opacity-50
                         dark:border-neutral-600 dark:hover:border-orange-400 dark:hover:bg-orange-950"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full
                                bg-orange-100 text-sm font-bold text-orange-500
                                dark:bg-orange-900 dark:text-orange-300">
                  $
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white">USDT</p>
                  <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                    Pay with Tether (USDT) from your balance
                  </p>
                </div>
              </div>
              <svg className="h-4 w-4 shrink-0 text-neutral-400" fill="none" stroke="currentColor"
                strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div className="flex w-full cursor-not-allowed flex-col rounded-xl border
                            border-neutral-200 bg-neutral-50 px-4 py-4 opacity-60
                            dark:border-neutral-700 dark:bg-neutral-800">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full
                                  bg-neutral-200 text-sm font-bold text-neutral-500
                                  dark:bg-neutral-700 dark:text-neutral-400">
                    ₦
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-neutral-900 dark:text-white">Fiat</p>
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium
                                       text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                        Not available yet
                      </span>
                    </div>
                    <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                      Pay with local currency (NGN, GHS, USD…)
                    </p>
                  </div>
                </div>
              </div>
              <p className="mt-3 border-t pt-3 text-xs text-amber-600 dark:border-neutral-700 dark:text-amber-400">
                Fiat payment is not available yet. Please use USDT to buy GHSX.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     VIEW: buy-rate  (user picks the rate)
  ============================================================ */
  if (view === "buy-rate") {
    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        <Header title="Choose a rate" onClick={() => { resetInput(); setView("buy-method"); }} />

        <div className="flex flex-col gap-6 p-6">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Select the rate you want to buy GHSX at.
          </p>

          <div className="flex flex-col gap-3">
            {options.map((o) => (
              <button
                key={o.key}
                onClick={() => handleSelectRate(o.key)}
                className="flex w-full items-center justify-between gap-3 rounded-xl border
                           border-neutral-300 px-4 py-4 text-left transition-colors
                           hover:border-orange-400 hover:bg-orange-50 active:bg-orange-100
                           dark:border-neutral-600 dark:hover:border-orange-400 dark:hover:bg-orange-950"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white">{o.label}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    1 GHSX = GH₵{o.rate}
                    {hasFx ? ` (${(o.rate / ghsPerUsdt).toFixed(4)} USDT)` : ""}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {o.min ? `Minimum purchase: ${o.min} GHSX` : "No minimum purchase"}
                  </p>
                </div>
                <svg className="h-4 w-4 shrink-0 text-neutral-400" fill="none" stroke="currentColor"
                  strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     VIEW: buy-input
  ============================================================ */
  if (view === "buy-input" && selected) {
    const numeric       = Number(buyAmount);
    const hasValidInput = buyAmount && numeric > 0;
    const totalUsdt     = hasValidInput ? costUsdt(numeric) : null;
    const totalGhs      = hasValidInput ? costGhs(numeric) : null;

    const handlePreview = () => {
      const msg = validateAmount();
      setErrors(msg ? { amount: msg } : {});
      if (msg) return;
      setBuyError(null);
      setView("buy-confirm");
    };

    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        <Header title="Buy GHSX" onClick={handleBackFromInput} />

        <div className="flex flex-col gap-6 p-6">
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4
                          dark:border-neutral-700 dark:bg-neutral-800">
            <div className="text-xs text-neutral-500 dark:text-neutral-400">Your USDT Balance</div>
            <div className="mt-1 text-xl font-bold text-green-600 dark:text-green-400">
              {Number(available).toFixed(2)}{" "}
              <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">USDT</span>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-white">
                GHSX Amount
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={buyAmount}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "" || /^\d+(\.\d{0,2})?$/.test(raw)) {
                      setBuyAmount(raw);
                      setErrors((er) => ({ ...er, amount: null }));
                      setBuyError(null);
                    }
                  }}
                  className={`w-full rounded-lg border px-4 py-2.5 pr-16 text-sm
                    bg-white text-neutral-900 [appearance:none] focus:outline-none focus:ring-2
                    dark:bg-neutral-900 dark:text-white
                    ${errors.amount
                      ? "border-red-400 focus:ring-red-400"
                      : "border-neutral-300 focus:ring-orange-400 dark:border-neutral-600"}`}
                />
                {available > 0 && hasFx && (
                  <button
                    type="button"
                    onClick={handleMax}
                    className="absolute right-2 top-1/2 -translate-y-1/2
                               rounded-md px-2 py-1 text-xs font-semibold
                               text-orange-500 hover:bg-orange-50
                               dark:text-orange-400 dark:hover:bg-neutral-700"
                  >
                    MAX
                  </button>
                )}
              </div>
              {errors.amount && (
                <div className="mt-1 text-xs text-red-500">{errors.amount}</div>
              )}
            </div>

            <div className="rounded-xl border bg-neutral-50 dark:border-neutral-700
                            dark:bg-neutral-800 divide-y dark:divide-neutral-700">
              <div className="flex justify-between px-4 py-3 text-sm">
                <span className="text-neutral-500">Rate ({selected.label})</span>
                <span className="font-medium text-neutral-700 dark:text-neutral-200">
                  GH₵{selected.rate}
                  {priceUsdt != null ? ` (${priceUsdt.toFixed(4)} USDT)` : ""}
                </span>
              </div>
              {minAmt && (
                <div className="flex justify-between px-4 py-3 text-sm">
                  <span className="text-neutral-500">Minimum</span>
                  <span className="font-medium text-neutral-700 dark:text-neutral-200">
                    {minAmt} GHSX
                  </span>
                </div>
              )}
              <div className="flex justify-between px-4 py-3 text-sm font-semibold">
                <span className="text-neutral-700 dark:text-neutral-200">You pay</span>
                <span className="text-orange-500">
                  {totalUsdt != null
                    ? `${fmtUsdt(totalUsdt)} USDT`
                    : totalGhs != null
                      ? `GH₵${totalGhs.toFixed(2)} in USDT`
                      : "— USDT"}
                </span>
              </div>
            </div>

            {buyError && <p className="text-xs text-red-500">{buyError}</p>}

            <button
              onClick={handlePreview}
              disabled={buyLoading || !hasValidInput}
              className="mt-2 flex items-center justify-center rounded-lg px-4 py-2.5
                         text-sm font-semibold bg-orange-400 text-white
                         hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {hasValidInput
                ? totalUsdt != null
                  ? `Preview: Buy ${numeric} GHSX for ${fmtUsdt(totalUsdt)} USDT`
                  : `Preview: Buy ${numeric} GHSX`
                : "Buy GHSX"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     VIEW: buy-confirm
  ============================================================ */
  if (view === "buy-confirm" && selected) {
    const numeric  = Number(buyAmount);
    const totalUsdt = costUsdt(numeric);
    const totalGhs  = costGhs(numeric);
    const afterBuy  = totalUsdt != null ? Number((available - totalUsdt).toFixed(2)) : null;

    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        <Header
          title="Confirm Purchase"
          onClick={() => { setBuyError(null); setView("buy-input"); }}
        />

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
          <p className="text-sm text-neutral-500">
            Please review your purchase details before confirming.
          </p>

          <div className="rounded-xl border bg-neutral-50 dark:border-neutral-700
                          dark:bg-neutral-800 divide-y dark:divide-neutral-700">
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">GHSX Amount</span>
              <strong className="text-orange-500">{numeric.toFixed(2)} GHSX</strong>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Rate ({selected.label})</span>
              <span className="text-neutral-600 dark:text-neutral-300">
                GH₵{selected.rate}
                {priceUsdt != null ? ` (${priceUsdt.toFixed(4)} USDT)` : ""}
              </span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Value</span>
              <span className="text-neutral-600 dark:text-neutral-300">
                GH₵{totalGhs.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Payment method</span>
              <span className="font-medium uppercase">USDT</span>
            </div>
            {totalUsdt != null && (
              <>
                <div className="flex justify-between px-4 py-3 text-sm font-semibold">
                  <span className="text-neutral-700 dark:text-neutral-200">Total to pay</span>
                  <span className="text-orange-500">{fmtUsdt(totalUsdt)} USDT</span>
                </div>
                <div className="flex justify-between px-4 py-3 text-sm">
                  <span className="text-neutral-500">USDT balance after</span>
                  <span className="font-semibold">{afterBuy.toFixed(2)} USDT</span>
                </div>
              </>
            )}
          </div>

          <div className="rounded-xl bg-amber-50 border border-amber-200
                          dark:bg-amber-950 dark:border-amber-800 px-4 py-3">
            <p className="text-xs text-amber-700 dark:text-amber-300">
              This action cannot be undone. Confirm the amount before proceeding.
            </p>
          </div>

          {buyError && <p className="text-xs text-red-500">{buyError}</p>}
        </div>

        <div className="border-t border-neutral-200 px-6 py-4 dark:border-neutral-700 flex gap-3">
          <button
            onClick={() => { setBuyError(null); setView("buy-input"); }}
            disabled={buyLoading}
            className="flex-1 rounded-lg border py-2.5 text-sm font-semibold
                       dark:border-neutral-600 hover:bg-neutral-50
                       dark:hover:bg-neutral-800 transition-colors disabled:opacity-40"
          >
            Edit
          </button>
          <button
            onClick={handleBuy}
            disabled={buyLoading}
            className="flex-1 rounded-lg bg-orange-400 py-2.5 text-sm font-semibold
                       text-white disabled:opacity-40 hover:bg-orange-500 transition-colors"
          >
            {buyLoading ? "Processing…" : "Confirm Purchase"}
          </button>
        </div>
      </div>
    );
  }

  /* ============================================================
     VIEW: buy-success
  ============================================================ */
  if (view === "buy-success" && buyResult) {
    const paid       = Number(buyResult.data?.totalCost);
    const gotAmount  = Number(buyResult.data?.amount);
    const rateLabel  = selected ? `GH₵${selected.rate} (${selected.label})` : "—";

    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        <div className="flex items-center gap-3 border-b border-neutral-200 px-6 py-4 dark:border-neutral-700">
          <h2 className="text-base font-semibold">Purchase Complete</h2>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center px-6 py-8">
          <div className="mb-5 flex h-16 w-16 items-center justify-center
                          rounded-full bg-orange-100 text-orange-500 text-3xl">
            ✓
          </div>
          <h3 className="text-xl font-bold mb-1">
            {gotAmount} GHSX received
          </h3>
          <p className="text-sm text-neutral-500 mb-8">Credited to your GHSX wallet</p>

          <div className="w-full rounded-xl border bg-neutral-50 dark:border-neutral-700
                          dark:bg-neutral-800 divide-y dark:divide-neutral-700 mb-6">
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">GHSX received</span>
              <strong>{gotAmount} GHSX</strong>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">USDT paid</span>
              <strong>{paid.toFixed(4)} USDT</strong>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Payment method</span>
              <span className="font-medium uppercase">USDT</span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Rate</span>
              <span>{rateLabel}</span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">New GHSX balance</span>
              <strong className="text-green-600">
                {Number(buyResult.data?.wallet?.balance).toFixed(2)} GHSX
              </strong>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">Available GHSX</span>
              <strong>
                {Number(
                  buyResult.data?.wallet?.available ??
                  buyResult.data?.wallet?.balance ??
                  0
                ).toFixed(2)} GHSX
              </strong>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500">USDT balance after</span>
              <span>{Number(buyResult.data?.usdtDeduction?.newBalance).toFixed(2)} USDT</span>
            </div>
          </div>

          <button
            onClick={onBack}
            className="w-full rounded-lg bg-orange-400 py-2.5 text-sm font-semibold text-white
                       hover:bg-orange-500"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return null;
}